import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import classNames from 'classnames';
import DOMPurify from 'dompurify';
import TimeAgo from 'timeago-react';
import { Grid, Collapse, Box } from '@mui/material';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import { DropzoneDialogBase } from '../../../components/MUI5Dropzone';

import { Alert, AlertTitle } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { getContact, saveContact, getTicket, createTicket, replyTicket } from '../../../modules/ticketsystem.js'
import RedactorX from '../../../components/RedactorXWrapper';

const styles = {
  /**
   * Chrome des Ticketsystems — folgt dem Modus. Die NACHRICHTENFLÄCHE bleibt
   * dagegen hell (siehe `message` weiter unten): die Inhalte sind
   * Freshdesk-E-Mail-HTML, und DOMPurify läuft hier ohne Konfiguration, lässt
   * also inline `style="color:…"`, `bgcolor` und `<font color>` durch. Auf
   * dunklem Grund könnte das dunkel-auf-dunkel ergeben, bei Inhalten die wir
   * nicht kontrollieren. Gleiche Abwägung wie beim Richtexteditor.
   */
  root: {
    position: 'relative',
    backgroundColor: 'background.paper',
    borderRadius: '1px',
    boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
  },
  mainContainer: {
    display: 'flex',
    minHeight: '75vh',
    position: 'relative',
    backgroundColor: 'background.paper',
    borderRadius: '1px',
    boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
  },
  drawer: {
    position: 'sticky',
    top: 0,
    width: 240,
    height: '100%',
    flexShrink: 0,
    float: 'left',
  },
  drawerPaper: {
    width: 240,
    left: 'auto',
    right: 'auto',
    top: 'auto',
    bottom: 'auto',
    position: 'relative',
    background: 'none',
  },
  content: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '-1px',
    borderLeft: '1px solid',
    borderColor: 'divider',
    width: 'calc(100% - 240px)',
  },
  appBar: {
    zIndex: 100,
    position: 'sticky',
    top: '40px',
    height: 65,
    backgroundColor: 'surfaceRecessed',
    color: 'text.primary',
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '4px',
  },
  appBarIcon: {
    marginTop: '-5px',
    width: '2rem',
    height: '2rem',
  },
  createNewTicketBtn: {
    position: 'absolute',
    right: '16px',
  },
  chatContainer: (theme) => ({
    flexGrow: 1,
    padding: theme.spacing(2),
  }),
  inputContainer: (theme) => ({
    position: 'sticky',
    bottom: '-30px',
    zIndex: 1,
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    '&.creation': {
      flexDirection: 'row',
    },
    '& .MuiCollapse-root': {
      width: '100%',
    },
  }),
  textField: (theme) => ({
    marginRight: theme.spacing(2),
    flexGrow: 1,
  }),
  contactCreationForm: {
    justifyContent: 'start !important',
  },
  fullHeightInfo: {
    display: 'flex',
    alignContent: 'center',
    justifyContent: 'center',
    height: '100%',
    flexWrap: 'wrap',
    flexDirection: 'column',
  },
  loadingSign: {},
  loadingSpinnerContainer: {
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    position: 'absolute',
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--ly-backdrop)',
  },
  /**
   * BEWUSST HELL in beiden Modi — entschieden, siehe Kommentar bei `root`.
   * Die Textfarbe muss mitgesetzt werden: ohne sie erbt der sanitisierte
   * Mail-Inhalt `text.primary` und wäre im Dark Mode weiss auf weiss.
   * Die Klasse `ly-light-surface` nimmt die Fläche von der globalen
   * Linkfarbe in index.css aus.
   */
  message: {
    padding: '24px 12px',
    backgroundColor: '#fff',
    color: 'rgba(0, 0, 0, 0.87)',
    '&.own': {
      backgroundColor: '#efefef',
      borderRadius: '8px',
      margin: '16px',
    },
  },
  ticketIdLabel: {
    position: 'absolute',
    right: '24px',
  },
  ticketDateLabel: {
    marginLeft: '16px',
  },
};

const Ticketsystem = (props) => {
  const { t } = useTranslation();
  const [loaded, setLoaded] = React.useState(false);
  const [contactCreationStep, setContactCreationStep] = React.useState(0);
  const [selectedTicket, setSelectedTicket] = React.useState(null);
  const [messages, setMessages] = React.useState([]);
  const [newFirstname, setNewFirstname] = React.useState('');
  const [newLastname, setNewLastname] = React.useState('');
  const [newEmail, setNewEmail] = React.useState('');
  const [newSubject, setNewSubject] = React.useState('');
  const [newMessage, setNewMessage] = React.useState('');
  const [creationMode, setCreationMode] = React.useState(false);
  const [showReplyForm, setShowReplyForm] = React.useState(false);
  const [showSpamInfo, setShowSpamInfo] = React.useState(false);
  const [showSuccessInfo, setShowSuccessInfo] = React.useState(false);
  const [showErrorInfo, setShowErrorInfo] = React.useState(false);
  const [openAttachmentsDialog, setOpenAttachmentsDialog] = React.useState(false);
  const [attachments, setAttachments] = React.useState([])

  const contact = useSelector((state) => state.ticketsystem.contact);
  const tickets = useSelector((state) => state.ticketsystem.tickets);
  const get_contact_pending = useSelector((state) => state.ticketsystem.get_contact_pending);
  const create_contact_pending = useSelector((state) => state.ticketsystem.create_contact_pending);
  const create_ticket_pending = useSelector((state) => state.ticketsystem.create_ticket_pending);
  const get_ticket_pending = useSelector((state) => state.ticketsystem.get_ticket_pending);
  const reply_ticket_pending = useSelector((state) => state.ticketsystem.reply_ticket_pending);
  
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  
  useEffect(() => {
    dispatch(
      getContact((res) => {
        setLoaded(true);

        if(res.success == false) {
          setContactCreationStep(0);
        } else {
          if(res.contact?.active === false) {
            setNewEmail(res.contact.email);
            setShowSpamInfo(true);
            setContactCreationStep(1);
          }
        }
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (id && tickets.length > 0 && selectedTicket === null && !loaded) {
      setTimeout(() => {
        let ticketIndex = tickets.findIndex(x => x.id === id);
        if (ticketIndex >= 0) {
          handleTicketSelect(ticketIndex);
        }
      }, 1000);
    }
  }, [tickets, id, selectedTicket, loaded]);

  const handleSaveContact = () => {
    dispatch(
      saveContact(
        newFirstname, 
        newLastname, 
        newEmail, 
        (res) => {
          if(res.success === true) {
            setContactCreationStep(1);
            setShowSuccessInfo(true);
            if(res.activationEMailSend === true){
              setShowSpamInfo(true);
            } else {
              // reload ticketsystem
              setTimeout(() => {
                props.doReloadComponent(Math.random()*100);
              }, 5000)
            }
          } else {
            setShowErrorInfo(true);
          }
        }
      )
    );
  }

  const handleCreateNewTicket = () => {
    setNewSubject('');
    setNewMessage('');
    setSelectedTicket(null);
    setShowSpamInfo(false);
    setShowSuccessInfo(false);
    setCreationMode(true);
  }

  const handleTicketSelect = (index) => {
    setCreationMode(false); 
    setShowReplyForm(false);
    dispatch(getTicket(tickets[index].id, () => {
      history.push(`/profile/tickets/${tickets[index].id}`);
      setSelectedTicket(tickets[index]);
    }));
  };

  const handleTicketDeselect = () => {
    setCreationMode(false); 
    setShowReplyForm(false);
    setSelectedTicket(null);
    history.push('/profile/tickets');
  };

  const handleSendMessage = () => {
    if (newMessage.trim() !== '') {
      if (creationMode) {
        const formData = new FormData();
        formData.append('subject', newSubject);
        formData.append('description', newMessage);
        attachments.forEach(attachment => {
          formData.append('attachments[]', attachment.file);
        });
        dispatch(createTicket(formData, () => {
          setMessages([...messages, newMessage]);
          setNewMessage('');
          setAttachments([]);
          setCreationMode(false); 
          setShowReplyForm(false);
        }));
      } else {
        const formData = new FormData();
        formData.append('body', newMessage);
        attachments.forEach(attachment => {
          formData.append('attachments[]', attachment.file);
        });
        dispatch(replyTicket(selectedTicket.id, formData, () => {
          setMessages([...messages, newMessage]);
          setNewMessage('');
          setAttachments([]);
          setCreationMode(false); 
          setShowReplyForm(false);
        }));
      }
    }
  };

  const sortTicketsByUpdatedAt = (tickets) => {
    return tickets.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
  }

  const handleShowReplyForm = () => {
    setShowReplyForm(true);
  }

  const handleHideReplyForm = () => {
    setShowReplyForm(false);
  }

  const downloadFile = (fileLink, fileName) => {
    const link = document.createElement('a');
    link.href = fileLink;  // Replace with your actual file link
    link.setAttribute('download', fileName); // You can specify a custom filename
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);  // Clean up the DOM
  };

  const renderContactForm = () => {
    return (
      <div style={{padding: 4}}>
        {contactCreationStep === 0 &&
          <Grid container spacing={2}>
            <Grid size={12}>
              <Typography variant="h4">
                {t('ticketsystem.createNewContact')}
              </Typography>
              <Typography variant="subtitle1">
                {t('ticketsystem.contactDescription')}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                variant="outlined"
                label={t('ticketsystem.firstname')}
                value={newFirstname}
                onChange={(e) => setNewFirstname(e.target.value)}
                sx={styles.textField}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                variant="outlined"
                label={t('ticketsystem.lastname')}
                value={newLastname}
                onChange={(e) => setNewLastname(e.target.value)}
                sx={styles.textField}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                variant="outlined"
                label={t('ticketsystem.email')}
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                sx={styles.textField}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />        
            </Grid>
            <Grid size={12} sx={{ alignContent: 'end' }}>
              <Button onClick={handleSaveContact} variant="contained" color="primary"
                disabled={newEmail === '' || newFirstname === '' || newLastname === ''}  
              >
                {t('ticketsystem.saveContact')}
              </Button>        
            </Grid>
          </Grid>
        }
        {contactCreationStep === 1 &&
          <>
            {showErrorInfo &&
              <Alert severity="error" elevation={3} gutterBottom style={{marginBottom: 16}}>
                <strong>{t('ticketsystem.contactCreatedSuccess')}</strong>
              </Alert>
            }
            {showSuccessInfo &&
              <Alert severity="success" elevation={3} gutterBottom style={{marginBottom: 16}}>
                <strong>{t('ticketsystem.contactCreatedSuccess')}</strong>
              </Alert>
            }
            {showSpamInfo &&
              <>
                <Alert severity="warning" elevation={3} gutterBottom style={{marginBottom: 16}}>
                  {t('ticketsystem.activationEmailSent', { email: newEmail })}<br/>
                  {t('ticketsystem.checkSpamFolder')}
                </Alert>
                <Button variant="contained" color="primary" onClick={() => {props.doReloadComponent(Math.random()*100);}}>
                  {t('ticketsystem.reloadTicketsystem')}
                </Button>
              </>
            }
          </>
        }
      </div>
    );
  }

  const renderReplyForm = (showingOnStartup) => {
    return (
      <Box className={classNames((showingOnStartup || showReplyForm) ? 'creation' : null)} sx={styles.inputContainer}>
        <Divider />
        <Collapse in={!showingOnStartup ? showReplyForm : true} fullWidth>
          <Grid spacing={2} container>
            {creationMode &&
              <Grid size={12}>
                <TextField
                  variant="outlined"
                  placeholder={t('ticketsystem.subjectPlaceholder')}
                  label={t('ticketsystem.subject')}
                  value={newSubject}
                  onChange={(e) => setNewSubject(e.target.value)}
                  sx={styles.textField}
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            }  
            <Grid size={12}>
              <RedactorX 
                config={{
                  plugins: ['alignment', 'removeformat', 'fontcolor', 'underline'],
                  buttons: {
                    addbar: ['paragraph', 'table', 'embed', 'quote', 'pre', 'line'],
                    editor: ['add', 'format', 'file'],
                    context: ['bold', 'italic', 'underline', 'link'],
                    toolbar: [],
                    topbar: ['html'],
                  },
                  addbar: {
                    add: [],
                    hide: ['quote', 'pre'],
                  },
                  subscribe: {
                    
                  }
                }}
                placeholder={t('ticketsystem.messagePlaceholder')}
                label={t('ticketsystem.message')}
                defaultValue={newMessage}
                onChange={(data) => { 
                  setNewMessage(data);
                }}
              /> 
            </Grid>
            <Grid size={12}>
              <ButtonGroup disableElevation variant="contained" size="small">
                <Button size="small" onClick={() => {setOpenAttachmentsDialog(true)}}>
                  <AttachFileOutlinedIcon /> {t('ticketsystem.attachments')} {` (${attachments.length})`}
                </Button>
                <Button
                  disabled={(create_ticket_pending || reply_ticket_pending || newMessage === '' || (newSubject === '' && creationMode))}
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={handleSendMessage}
                >
                  {creationMode ? <AddOutlinedIcon /> : <ReplyOutlinedIcon />}
                  {creationMode ? t('ticketsystem.create') : t('ticketsystem.reply')}
                </Button>  
              </ButtonGroup>        
            </Grid>
          </Grid>   

          
          <DropzoneDialogBase
            //dialogTitle={dialogTitle()}
            acceptedFiles={['image/*', 'video/*', 'application/zip']}
            fileObjects={attachments}
            cancelButtonText={t('ticketsystem.cancel')}
            submitButtonText={t('ticketsystem.addAttachment')}
            maxFileSize={2000000}
            open={openAttachmentsDialog}
            onAdd={newFileObjs => {
              setAttachments([].concat(attachments, newFileObjs));
            }}
            onDelete={deleteFileObj => {
              setAttachments(attachments.filter(x => x.file.name !== deleteFileObj.file.name));
            }}
            onClose={() => setOpenAttachmentsDialog(false)}
            onSave={() => {
              setOpenAttachmentsDialog(false);
            }}
            showPreviews={true}
            showFileNamesInPreview={true}
          />    
        </Collapse>

        {!showReplyForm && !showingOnStartup &&
          <Grid spacing={2} container>
            <Grid size={12}>
              <Button variant="contained" color="primary" onClick={handleShowReplyForm}>
                {t('ticketsystem.writeReply')}
              </Button>
            </Grid>
          </Grid>
        }
      </Box>
    );
  }

  return (
    <Box sx={styles.root}>
      <AppBar position="fixed" sx={styles.appBar}>
        <Toolbar>
          <Typography variant="h6" noWrap>
            {selectedTicket === null ?
              <>
                {creationMode ?
                  <><IconButton onClick={handleTicketDeselect} size="large"><ArrowBackOutlinedIcon /></IconButton> {t('ticketsystem.createTicket')}</>
                :
                  <><ConfirmationNumberOutlinedIcon sx={styles.appBarIcon} /> {t('ticketsystem.title')}</>
                }
              </>
            :
              <><IconButton onClick={handleTicketDeselect} size="large"><ArrowBackOutlinedIcon /></IconButton> {selectedTicket.subject}</>
            }
          </Typography>
          {selectedTicket !== null &&
            <>
              <Typography align="right" variant="body2" sx={styles.ticketDateLabel}>
                <TimeAgo datetime={selectedTicket.updated_at} />
              </Typography>
              <Typography align="right" variant="body2" sx={styles.ticketIdLabel}>
                {` #${selectedTicket.id}`}
              </Typography>            
            </>
          }
          {contact && contact.active && !creationMode && selectedTicket === null &&
            <Button sx={styles.createNewTicketBtn} variant="contained" color="primary" onClick={handleCreateNewTicket}>
              <AddOutlinedIcon /> {t('ticketsystem.createNewTicket')}
            </Button>
          }
        </Toolbar>
      </AppBar>
      <Box sx={styles.mainContainer}>
        <Drawer
          sx={styles.drawer}
          variant="permanent"
          PaperProps={{ sx: styles.drawerPaper }}
        >
          <List>
            {sortTicketsByUpdatedAt(tickets).map((ticket, index) => (
              <ListItem button key={`ticket-${ticket.id}`} onClick={() => handleTicketSelect(index)} selected={ticket.id === selectedTicket?.id}>
                <ListItemText 
                  primary={<>{ticket.subject}<small>{` #${ticket.id}`}</small></>} 
                  primaryTypographyProps={{noWrap: true}}
                  secondary={<TimeAgo datetime={ticket.updated_at} />} 
                  secondaryTypographyProps={{noWrap: true}}
                />
              </ListItem>
            ))}
          </List>
        </Drawer>
        {loaded && <Box component="main" sx={styles.content}>
          {!creationMode && selectedTicket !== null &&
            <Box sx={styles.chatContainer}>
              <Box className="ly-light-surface" sx={styles.message}>
                <Typography variant={'caption'}>
                  <strong>{t('ticketsystem.ticketCreatedAt')} </strong>
                  <TimeAgo datetime={selectedTicket.created_at} />
                </Typography>
                <Divider />
                <Box className="ly-light-surface" sx={styles.message} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedTicket?.ticket?.description) }} />
                {selectedTicket?.ticket?.attachments?.length > 0 &&
                  <>
                    <Divider/>
                    <Typography variant="caption" component="p">Attachments:</Typography>
                    {selectedTicket.ticket.attachments.map(attachment => <Typography variant="caption" component="p">
                      <a href={attachment.attachment_url} target="_blank" download={attachment.name}
                        //onClick={() => {
                        //downloadFile(attachment.attachment_url, attachment.name);
                        //}}
                      >{attachment.name}</a>
                    </Typography>)}
                  </>
                }
              </Box>
              {selectedTicket?.conversations && selectedTicket.conversations.map((message, index) => (
                <Box className={classNames('ly-light-surface', message.user_id === contact.id ? 'own' : null)} sx={styles.message} key={index}>
                  <Typography variant={'caption'}>
                    <strong>{message.user_id === contact.id ? t('ticketsystem.repliedAt') : t('ticketsystem.respondedAt')} </strong>
                    <TimeAgo datetime={message.updated_at} />
                  </Typography>
                  <Divider />
                  <Box className="ly-light-surface" sx={styles.message} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message.body) }} />
                  {message?.attachments?.length > 0 &&
                    <>
                      <Divider/>
                      <Typography variant="caption" component="p">Attachments:</Typography>
                      {message.attachments.map(attachment => <Typography variant="caption" component="p">
                        <a href={attachment.attachment_url} target="_blank" download={attachment.name}
                          //onClick={() => {
                          //downloadFile(attachment.attachment_url, attachment.name);
                          //}}
                        >{attachment.name}</a>
                      </Typography>)}
                    </>
                  }
                </Box>
              ))}
            </Box>
          }
          
          {(!contact || contact.active === false) &&
            <Box sx={[styles.contactCreationForm, styles.fullHeightInfo, { padding: '16px' }]}>
              { renderContactForm() }
            </Box>
          }

          {contact && contact.active === true && !creationMode && selectedTicket === null && tickets.length > 0 && 
            <Typography sx={styles.fullHeightInfo}>{t('ticketsystem.noTicketSelected')}</Typography>
          }
          
          {contact && contact.active === true && !creationMode && selectedTicket === null && tickets.length === 0 && 
            <Box sx={styles.fullHeightInfo}>
              <Typography gutterBottom>{t('ticketsystem.noTicketCreated')}</Typography>
              <Typography align="center">
                <Button variant="contained" color="primary" onClick={handleCreateNewTicket}>
                  {t('ticketsystem.createNewTicket')}
                </Button>
              </Typography>
            </Box>
          }

          {(creationMode || selectedTicket !== null) &&
            renderReplyForm(creationMode)
          }

        </Box>}
        {/* outside the loaded block: the first contact request left the paper blank until it returned */}
        {(get_contact_pending || create_contact_pending || create_ticket_pending || get_ticket_pending || reply_ticket_pending) && 
          <Box component="span" sx={styles.loadingSpinnerContainer}>
            <CircularProgress sx={styles.loadingSign} size={25} />
          </Box>
        }
      </Box>
    </Box>
  );
};

export default Ticketsystem;
